import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Plus } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api, authed, errMsg } from "@/lib/api";

const NewCategory = ({ onCreated }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [prefix, setPrefix] = useState("JB");
  const create = async () => {
    try {
      const { data } = await api.post("/categories", { name, code_prefix: prefix });
      toast.success(`${data.name} catalog created`);
      setOpen(false); setName(""); onCreated();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="h-11 px-6 text-xs tracking-[0.2em] uppercase" data-testid="new-category-button"><Plus size={14} className="mr-2" />New category</Button>
      </DialogTrigger>
      <DialogContent className="bg-[var(--cream)]">
        <DialogHeader><DialogTitle>New category catalog</DialogTitle></DialogHeader>
        <label className="text-[11px] tracking-[0.2em] uppercase">Name (e.g. Beds)</label>
        <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="new-category-name-input" />
        <label className="text-[11px] tracking-[0.2em] uppercase">Product code prefix (e.g. JBB)</label>
        <Input value={prefix} onChange={(e) => setPrefix(e.target.value.toUpperCase())} data-testid="new-category-prefix-input" />
        <Button disabled={!name.trim()} onClick={create} data-testid="new-category-submit">Create catalog</Button>
      </DialogContent>
    </Dialog>
  );
};

const CategoryCard = ({ c, i }) => (
  <Link to={`/category/${c.id}`} className="group block rise" style={{ animationDelay: `${i * 80}ms` }} data-testid={`category-card-${c.id}`}>
    <div className="relative aspect-[16/9] overflow-hidden bg-[var(--line)] page-shadow">
      <img src={authed(`/categories/${c.id}/preview/cover.png`)} alt={c.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
    </div>
    <div className="flex items-end justify-between pt-5 border-b border-[var(--line)] pb-4">
      <div>
        <p className="text-[11px] tracking-[0.25em] uppercase text-[var(--muted-ink)]">{c.edition}</p>
        <h3 className="text-2xl font-bold mt-1">{c.name}</h3>
      </div>
      <div className="text-right">
        <p className="text-sm" data-testid={`category-count-${c.id}`}>{c.product_count} products · {c.product_count * 2 + 3} pages</p>
        <ArrowUpRight className="ml-auto mt-1 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" size={18} />
      </div>
    </div>
  </Link>
);

export default function Dashboard() {
  const [cats, setCats] = useState(null);
  const load = () => api.get("/categories").then((r) => setCats(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
        <div className="rise">
          <p className="text-[11px] tracking-[0.3em] uppercase text-[var(--muted-ink)] mb-3">Category catalogs</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">Catalogs</h1>
          <p className="text-sm md:text-base text-[var(--muted-ink)] mt-4 max-w-xl">Every product you add becomes two pages — the image set and the configuration &amp; price sheet — slotted between the opening and closing pages.</p>
        </div>
        <NewCategory onCreated={load} />
      </div>
      {cats === null ? <p className="text-sm text-[var(--muted-ink)]">Loading…</p> : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-x-10 gap-y-14" data-testid="category-grid">
          {cats.map((c, i) => <CategoryCard key={c.id} c={c} i={i} />)}
        </div>
      )}
    </div>
  );
}
